/** A single property value on a data node, as returned by the `nodes` listing endpoint. */
export interface DataNodePropertyValue {
  type: string;
  isPrimary: boolean;
  isAutoId: boolean;
  value: unknown;
}

/**
 * A data node instance of a given blueprint (template) node type.
 * Returned by `GET /projects/{projectId}/nodes?technicalName=…` and the `…/connected` listing.
 */
export interface DataNode {
  hasChildren: boolean | null;
  id: string;
  /** Keyed by property technical name. Includes `isPrimary` / `isAutoId` flags per value. */
  properties: Record<string, DataNodePropertyValue>;
}

/** Pull the auto-id and primary values off a node's property bag, driven by the per-value flags. */
export function getAutoIdAndPrimary(properties: Record<string, DataNodePropertyValue>) {
  const values = Object.values(properties);
  return {
    autoId: values.find((property) => property.isAutoId)?.value,
    primary: values.find((property) => property.isPrimary)?.value
  };
}

/**
 * A single data node fetched by id. The caller supplies the node's blueprint type (`templateNodeId`)
 * on the request, so it isn't echoed back here. Returned by `GET /projects/{projectId}/nodes/{dataNodeId}`.
 */
export interface DataNodeDetail {
  id: string;
  properties: Record<string, DataNodePropertyValue>;
}

export type ConnectedNodesOrderDirection = 'Asc' | 'Desc';

/** Query params for the paginated `GET /projects/{projectId}/nodes/{parentDataNodeId}/connected` endpoint. */
export interface GetConnectedNodesParams {
  /** Technical name of the parent (focused) node's type. */
  parentNodeTechnicalName: string;
  /** Blueprint relation to traverse. */
  relationId: string;
  /** Technical name of the connected (target) node type to return. */
  technicalName: string;
  /** Traverse the relation from target back to parent (a reverse relation from the focused node's POV). */
  isReverse: boolean;
  offset: number;
  /** Page size (1–100). */
  limit: number;
  /** Property to sort by — required by the backend; defaults to `Id` for stable paging. */
  orderBy?: string;
  orderDirection?: ConnectedNodesOrderDirection;
  proposalId?: string;
}
